// src/pages/HistoricalGallery.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  fetchGalleryFilters, 
  loadPhotosForCategory,
  photoCache,
  categoryManager
} from '../data/mockData';
import { useLiveVisitors, useVenueStats, useTracker } from '../hooks/useAnalytics';

// Componente per il skeleton loader delle immagini
const ImageSkeleton = () => (
  <div className="bg-stone-800 rounded-lg overflow-hidden shadow-md animate-pulse">
    <div className="relative">
      {/* Skeleton per l'immagine */}
      <div className="w-full aspect-square bg-stone-700 flex items-center justify-center">
        <svg className="w-12 h-12 text-stone-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
        </svg>
      </div>
      
      {/* Skeleton per l'anno */}
      <div className="absolute top-2 right-2 bg-stone-600 px-2 py-1 rounded">
        <div className="w-8 h-3 bg-stone-500 rounded"></div>
      </div>
      
      {/* Skeleton per il titolo e descrizione */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
        <div className="w-3/4 h-4 bg-stone-600 rounded mb-2"></div>
        <div className="w-full h-3 bg-stone-600 rounded mb-1"></div>
        <div className="w-2/3 h-3 bg-stone-600 rounded"></div>
      </div>
    </div>
  </div>
);

// Componente per una singola immagine con stato di caricamento
const PhotoItem = ({ photo, onClick }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = (e) => {
    e.target.src = photo.fullSize;
    setImageLoaded(true);
  };

  return (
    <div 
      className="bg-stone-800 rounded-lg overflow-hidden shadow-md cursor-pointer hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105 relative"
      onClick={() => onClick(photo)}
    >
      {/* Skeleton loader - visibile fino al caricamento dell'immagine */}
      {!imageLoaded && (
        <div className="absolute inset-0 z-10">
          <ImageSkeleton />
        </div>
      )}
      
      {/* Contenuto reale dell'immagine */}
      <div className={`relative transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}>
        <img 
          src={photo.thumbnail} 
          alt={photo.title} 
          className="w-full aspect-square object-cover" 
          loading="lazy"
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
        
        {photo.year && (
          <div className="absolute top-2 right-2 bg-stone-900/70 text-amber-300 px-2 py-1 rounded text-xs font-bold">
            {photo.year}
          </div>
        )}
        
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
          <h3 className="font-bold text-amber-300 text-sm mb-1">{photo.title}</h3>
          {photo.description && (
            <p className="text-amber-100 text-xs line-clamp-2">{photo.description}</p>
          )}
        </div>
        
        {/* Indicatore di errore di caricamento */}
        {imageError && (
          <div className="absolute top-2 left-2 bg-red-600/80 text-white px-2 py-1 rounded text-xs">
            <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Errore
          </div>
        )}
      </div>
    </div>
  );
};

// Historical Gallery Component con skeleton loading
const HistoricalGallery = ({ navigateTo, setSelectedPhoto, savedGalleryState, setSavedGalleryState }) => {
  // Stati per i dati
  const [photos, setPhotos] = useState([]);
  const [filters, setFilters] = useState([{ id: 'all', label: 'Tutti' }]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [error, setError] = useState(null);
  const visitors = useLiveVisitors();
  const stats = useVenueStats();
  const { trackPhotoView, trackCustom } = useTracker();
  // Ref per il container principale per gestire lo scroll
  const containerRef = useRef(null);
  
  // Stati per la paginazione
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  
  // Ripristina lo stato salvato se disponibile
  useEffect(() => {
    if (savedGalleryState) {
      console.log('Ripristino stato salvato della galleria:', savedGalleryState);
      setActiveFilter(savedGalleryState.activeFilter);
      setPhotos(savedGalleryState.photos);
      setPagination(savedGalleryState.pagination);
      
      // Ripristina la posizione di scroll dopo un breve delay
      setTimeout(() => {
        if (containerRef.current && savedGalleryState.scrollPosition) {
          window.scrollTo(0, savedGalleryState.scrollPosition);
        }
      }, 100);
      
      // Pulisci lo stato salvato dopo averlo ripristinato
      setSavedGalleryState(null);
      setLoading(false);
      return;
    }
  }, [savedGalleryState, setSavedGalleryState]);
  useEffect(() => {
    trackCustom('gallery_view', {
      entry_point: 'navigation',
      device: window.innerWidth < 768 ? 'mobile' : 'desktop'
    });
  }, []);
  // Carica i filtri all'avvio del componente (solo se non c'è stato salvato)
  useEffect(() => {
    if (savedGalleryState) return; // Non caricare se stiamo ripristinando lo stato
    
    const loadFilters = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Loading gallery filters...');
        const filterData = await fetchGalleryFilters();
        
        console.log('Loaded filters:', filterData);
        setFilters(filterData);
        
      } catch (err) {
        console.error('Error loading gallery filters:', err);
        setError('Si è verificato un errore nel caricamento dei filtri. Riprova più tardi.');
      } finally {
        setLoading(false);
      }
    };
    
    loadFilters();
  }, [savedGalleryState]);
  
  // Funzione per caricare le foto per una categoria specifica
  const loadPhotosForFilter = useCallback(async (filter, page = 1, append = false) => {
    try {
      setLoadingPhotos(true);
      setError(null);
      
      console.log(`Loading photos for filter: ${filter}, page: ${page}`);
      
      const result = await loadPhotosForCategory(filter, page, 20);
      
      console.log('Loaded photos:', result);
      
      if (append) {
        // Aggiungi alle foto esistenti (per "Carica altro")
        setPhotos(prev => [...prev, ...result.photos]);
      } else {
        // Sostituisci le foto esistenti (nuovo filtro)
        setPhotos(result.photos);
      }
      
      setPagination(result.pagination);
      
    } catch (err) {
      console.error('Error loading photos for filter:', err);
      setError('Si è verificato un errore nel caricamento delle immagini. Riprova più tardi.');
    } finally {
      setLoadingPhotos(false);
    }
  }, []);
  
  // Gestisce il cambio di filtro
  const handleFilterChange = async (filterId) => {
    console.log('Changing filter to:', filterId);
    
    if (filterId === activeFilter) return;
    trackCustom('filter_change', {
      from: activeFilter,
      to: filterId,
      filterLabel: filters.find(f => f.id === filterId)?.label
    });
    setActiveFilter(filterId);
    
    // Se è "Tutti", non caricare foto inizialmente
    if (filterId === 'all') {
      setPhotos([]);
      setPagination({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
      return;
    }
    
    // Carica le foto per il nuovo filtro
    await loadPhotosForFilter(filterId, 1, false);
  };
  
  // Gestisce il caricamento di più foto (paginazione)
  const handleLoadMore = async () => {
    if (!pagination.hasNext || loadingPhotos || activeFilter === 'all') return;
    
    const nextPage = pagination.page + 1;
    await loadPhotosForFilter(activeFilter, nextPage, true);
  };
  
  // Gestisce la selezione di una foto
  const handlePhotoClick = (photo) => {
    console.log('Selected photo:', photo);
    trackPhotoView(photo);
    // Salva lo stato corrente della galleria
    const currentState = {
      activeFilter,
      photos,
      pagination,
      scrollPosition: window.pageYOffset || document.documentElement.scrollTop
    };
    
    console.log('Salvo stato galleria:', currentState);
    setSavedGalleryState(currentState);
    
    setSelectedPhoto(photo);
    navigateTo('photoDetail');
  };
  
  // Genera skeleton loaders per il caricamento iniziale
  const renderSkeletonGrid = (count = 6) => {
    return Array.from({ length: count }, (_, index) => (
      <ImageSkeleton key={`skeleton-${index}`} />
    ));
  };
  
  // Mostra un messaggio di caricamento iniziale
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6 flex justify-center items-center h-64">
        <div className="text-amber-300 text-center">
          <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-amber-500 inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <div className="font-medium">Caricamento dei filtri...</div>
          <div className="text-sm text-amber-100 mt-2">Connessione al server in corso...</div>
        </div>
      </div>
    );
  }
  
  // Mostra un messaggio di errore
  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="bg-red-900/50 border border-red-700 text-red-100 p-4 rounded-lg" role="alert">
          <div className="flex">
            <svg className="flex-shrink-0 h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium">Errore di caricamento</h3>
              <p className="text-sm mt-1">{error}</p>
              <div className="mt-3">
                <button 
                  onClick={() => window.location.reload()} 
                  className="bg-red-700 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Riprova
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div ref={containerRef} className="pb-6">
      {/* Header fisso */}
      <div className="bg-stone-900/95 backdrop-blur-sm border-b border-stone-700 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <h2 className="text-xl font-bold mb-4 text-center text-amber-300">Galleria Storica</h2>
          
          {/* Opzioni di filtro sticky */}
          <div className="flex overflow-x-auto py-2 gap-2 scrollbar-hide">
            {filters.map(filter => (
              <button
                key={filter.id}
                onClick={() => handleFilterChange(filter.id)}
                className={`px-4 py-2 text-sm whitespace-nowrap rounded-full transition-all flex-shrink-0 ${
                  activeFilter === filter.id 
                    ? 'bg-amber-500 text-stone-900 font-bold shadow-lg transform scale-105' 
                    : 'bg-stone-800 text-amber-100 hover:bg-stone-700'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contenuto principale */}
      <div className="container mx-auto px-4 pt-6">
      
        {/* Messaggio per filtro "Tutti" */}
        {activeFilter === 'all' && (
          <div className="text-center py-12 text-amber-200">
            <svg className="mx-auto h-16 w-16 text-amber-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h3 className="text-lg font-medium mb-2">Seleziona una categoria</h3>
            <p className="text-amber-300">Scegli una categoria dai filtri sopra per visualizzare le foto storiche.</p>
          </div>
        )}
        
        {/* Stato di caricamento foto - mostra skeleton */}
        {loadingPhotos && photos.length === 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {renderSkeletonGrid(6)}
          </div>
        )}
        
        {/* Messaggio nessuna foto trovata */}
        {!loadingPhotos && photos.length === 0 && activeFilter !== 'all' && (
          <div className="text-center py-12 text-amber-200">
            <svg className="mx-auto h-12 w-12 text-stone-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p>Nessuna foto trovata per la categoria "{filters.find(f => f.id === activeFilter)?.label}".</p>
            <button 
              onClick={() => setActiveFilter('all')}
              className="mt-2 text-amber-400 hover:text-amber-300 underline"
            >
              Torna ai filtri
            </button>
          </div>
        )}
        
        {/* Griglia delle foto */}
        {photos.length > 0 && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {photos.map(photo => (
                <PhotoItem
                  key={photo.id}
                  photo={photo}
                  onClick={handlePhotoClick}
                />
              ))}
            </div>
            
            {/* Skeleton per "Carica altro" durante il caricamento */}
            {loadingPhotos && pagination.hasNext && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                {renderSkeletonGrid(3)}
              </div>
            )}
            
            {/* Pulsante "Carica altro" */}
            {pagination.hasNext && !loadingPhotos && (
              <div className="text-center mt-8">
                <button
                  onClick={handleLoadMore}
                  className="px-6 py-3 rounded-lg font-medium transition-all bg-amber-600 hover:bg-amber-500 text-white hover:shadow-lg transform hover:scale-105"
                >
                  Carica altre foto ({pagination.total - photos.length} rimanenti)
                </button>
              </div>
            )}
            
            {/* Informazioni footer */}
            <div className="mt-6 text-center text-amber-200 text-sm">
              Visualizzate {photos.length} di {pagination.total} foto
              {pagination.totalPages > 1 && ` (Pagina ${pagination.page} di ${pagination.totalPages})`}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HistoricalGallery;
